import React, { useState, useCallback, useRef } from 'react';
import { useModal } from 'react-brave-modal';
import { isValid, parseISO } from 'date-fns';

import useQuery from '../../../../hooks/query';

import { GridCardProps } from '../../../../components/GridCard';
import ButtonFilterDate from '../../../../components/ButtonFilterDate';
import Button from '../../../../components/Button';

import BloodOxygenationChart from './Charts/BloodOxygenationChart';
import RespiratoryRateChart from './Charts/RespiratoryRateChart';
import BloodPressureChart from './Charts/BloodPressureChart';
import BodyTemperatureChart from './Charts/BodyTemperatureChart';
import CalorieChart from './Charts/CalorieChart';
import HeartRateChart from './Charts/HeartRateChart';
import SleepChart from './Charts/SleepChart';
import StepChart from './Charts/StepChart';
import EvolutionModal from './Modals/EvolutionModal';

import {
  Container,
  Header,
  NavigationArea,
  Title,
  Types,
  TypeItem,
} from './styles';
import GlucoseChart from './Charts/GlucoseChart';

type EvolutionProps = GridCardProps;

type ApiMetricType =
  | 'heartBeatRate'
  | 'bloodPressure'
  | 'bodyTemperature'
  | 'bloodOxygen'
  | 'sleep'
  | 'steps'
  | 'calories'
  | 'respiratoryRate'
  | 'glucose';

type ChartComponentType = {
  [key in ApiMetricType]: JSX.Element;
};

export interface ChildrenProps {
  dateFilter: Date;
}

export interface ChildrenRefProps {
  reload: () => Promise<void>;
}

export interface DataChildrenProps {
  lastRecordTime: string;
  value: string;
}

const API_METRICS: ApiMetricType[] = [
  'heartBeatRate',
  'bloodPressure',
  'bodyTemperature',
  'bloodOxygen',
  'sleep',
  'steps',
  'calories',
  'respiratoryRate',
  'glucose',
];

/**
 * A métrica e o dia pedidos na URL (`?metric=bloodPressure&date=2026-09-18`).
 *
 * ⓘ **Quem manda é a home de Alertas.** Vindo de um alerta de pressão, o card abria
 * em frequência cardíaca e no dia de hoje — "sem dados" —, e a enfermeira podia
 * concluir que o alerta era falso (achado 11 do QA de 18/09). Valor fora da lista
 * ou data inválida caem no padrão de antes, sem erro.
 */
const initialMetric = (value: string | null): ApiMetricType =>
  API_METRICS.find((metric) => metric === value) ?? 'heartBeatRate';

const initialDate = (value: string | null): Date => {
  const parsed = value ? parseISO(value) : null;

  return parsed && isValid(parsed) ? parsed : new Date();
};

const Evolution: React.FC<EvolutionProps> = ({ gridArea }) => {
  const query = useQuery();
  const { showModal } = useModal();
  const chartRef = useRef<ChildrenRefProps>(null);
  // const [measurementLoading, setMeasurementLoading] = useState(false);
  // const { user } = useAuth();

  const [apiMetric, setApiMetric] = useState<ApiMetricType>(() =>
    initialMetric(query.get('metric')),
  );
  const [dateFilter, setDateFilter] = useState(() =>
    initialDate(query.get('date')),
  );

  const chartComponent: ChartComponentType = {
    heartBeatRate: <HeartRateChart dateFilter={dateFilter} ref={chartRef} />,
    bloodPressure: (
      <BloodPressureChart dateFilter={dateFilter} ref={chartRef} />
    ),
    bloodOxygen: (
      <BloodOxygenationChart dateFilter={dateFilter} ref={chartRef} />
    ),
    bodyTemperature: (
      <BodyTemperatureChart dateFilter={dateFilter} ref={chartRef} />
    ),
    respiratoryRate: (
      <RespiratoryRateChart dateFilter={dateFilter} ref={chartRef} />
    ),
    calories: <CalorieChart dateFilter={dateFilter} ref={chartRef} />,
    sleep: <SleepChart dateFilter={dateFilter} ref={chartRef} />,
    steps: <StepChart dateFilter={dateFilter} ref={chartRef} />,
    glucose: <GlucoseChart dateFilter={dateFilter} ref={chartRef} />,
  };

  const handleSelection = useCallback((metricType: ApiMetricType) => {
    setApiMetric(metricType);
  }, []);

  const handleShowEvolutionModal = useCallback(() => {
    showModal({
      type: 'custom',
      data: <EvolutionModal />,
    });
  }, [showModal]);

  // A "Nova medicao" (`POST /measurements/run`) saiu na **4.9**: era o pedido de
  // afericao por push ao app, e o botao ja estava comentado aqui havia tempo -- o
  // modulo `measurements` inteiro foi removido do `cinco-back`, junto com a tabela
  // `manual_measurement_histories` que guardava o pedido. Se a funcionalidade voltar,
  // volta como decisao nova, e nao descomentando isto.

  // ⚠️ O `useEffect` que ficava aqui existia para o **contador** de "proxima
  // medicao", e recarregava o grafico no `else` dele. Com a afericao manual removida
  // (4.9) nao sobrou dependencia nenhuma: um efeito com lista vazia recarregaria o
  // grafico uma vez na montagem, o que o proprio `Chart` ja faz. Some inteiro.

  return (
    <Container gridArea={gridArea}>
      <Header>
        <Title>Evolução</Title>
      </Header>
      <NavigationArea>
        <Types>
          <TypeItem
            selected={apiMetric === 'heartBeatRate'}
            onClick={() => handleSelection('heartBeatRate')}
          >
            BPM
          </TypeItem>
          <TypeItem
            selected={apiMetric === 'bloodPressure'}
            onClick={() => handleSelection('bloodPressure')}
          >
            P.A
          </TypeItem>
          <TypeItem
            selected={apiMetric === 'bodyTemperature'}
            onClick={() => handleSelection('bodyTemperature')}
          >
            °C
          </TypeItem>

          <TypeItem
            selected={apiMetric === 'bloodOxygen'}
            onClick={() => handleSelection('bloodOxygen')}
          >
            OX
          </TypeItem>

          <TypeItem
            selected={apiMetric === 'sleep'}
            onClick={() => handleSelection('sleep')}
          >
            Sono
          </TypeItem>
          <TypeItem
            selected={apiMetric === 'steps'}
            onClick={() => handleSelection('steps')}
          >
            Passos
          </TypeItem>
          <TypeItem
            selected={apiMetric === 'calories'}
            onClick={() => handleSelection('calories')}
          >
            Cal
          </TypeItem>
          <TypeItem
            selected={apiMetric === 'respiratoryRate'}
            onClick={() => handleSelection('respiratoryRate')}
          >
            FR
          </TypeItem>
          <TypeItem
            selected={apiMetric === 'glucose'}
            onClick={() => handleSelection('glucose')}
          >
            Glic
          </TypeItem>
        </Types>
        <ButtonFilterDate
          onChange={({ date }) =>
            setDateFilter(new Date(date.setHours(23, 59, 59, 0)))
          }
          dateValue={{ date: dateFilter }}
        />
      </NavigationArea>

      {chartComponent[apiMetric]}

      <Button
        type="button"
        buttonType="primary-alternative"
        onClick={handleShowEvolutionModal}
      >
        Ver evolução detalhada
      </Button>
    </Container>
  );
};

export default Evolution;
